import React, { useCallback, useMemo, useRef } from 'react';
import { client } from '../../utils/trpc-client';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import {
  AdvancedFilterModel,
  ColGroupDef,
  ColumnRowGroupChangedEvent,
  FilterModel,
  GridApi,
  GridReadyEvent,
  KeyCreatorParams,
  SortModelItem,
  StatusPanelDef,
  ValueFormatterParams
} from '@ag-grid-community/core';
import { AgGridReact, CustomCellRendererProps, CustomTooltipProps } from '@ag-grid-community/react';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '../shared/page-title';
import {
  allChannelsWithTactics,
  allUnitPriceTypes,
  Channel,
  Platform
} from 'shared/src/domain/line-item/line-item-channels';
import {
  LineItemListRow,
  LineItemOverallResult,
  LineItemPlan,
  LineItemsFilterTypes,
  LineItemSort,
  LineItemSortColumn,
  lineItemSortColumns,
  LineItemTableRow,
  lineItemTypeDisplayNames,
  PartialLineItem
} from 'shared/src/domain/line-item/line-item-types';
import { useChannelRatesContext } from '../strategy/channel-rates-context';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { SearchRow } from '../../components/display/search-bar';
import { showErrorToast } from '../../components/feedback/error-toast';
import {
  currencyValue,
  dateValue,
  defaultColDef,
  formatCurrency,
  numberFilter,
  numericalValue,
  percentFilter,
  percentValue,
  textFilter
} from '../../components/tables/table-utils';
import { AllLineItemsIcon, PlatformBuyIcon } from '../../components/layout/nav-icons';
import { TotalStatusPanel } from '../shared/totals-status-panel';
import type { RowClickedEvent } from '@ag-grid-community/core/dist/types/src/events';
import { useCaptureListingPageView } from '../../utils/posthog/analytics';
import {
  DoneOMeterTooltip,
  PendingPropertyIndicator
} from '../../components/tables/cell-renderers/utils';
import { PacingIndicator } from '../../components/tables/cell-renderers/pacing-indicator';
import { PercentCompletedIndicator } from '../../components/tables/cell-renderers/percent-completed-indicator';
import {
  LineItemPlanStatusTooltip,
  lineItemPlanStatusTooltipValue
} from '../../components/feedback/plan-status-tooltip';
import {
  LineItemPlanStatusIndicator,
  planStatusFilterValues
} from '../../components/tables/cell-renderers/plan-status-indicators';
import { zohoCampaignStatusValues } from '../../../../shared/src/domain/zoho/zoho-types';
import { mapKeys } from 'lodash';
import { parseFilterModelPerKey } from 'shared/src/utils/filter/parse-filter-model';
import { seniorMediaTraderRoles } from 'shared/src/utils/user/user-utils';
import {
  ActiveLineItemMediaPlans,
  ActiveLineItemMediaPlansTooltip,
  activePlatformBuysFilterValues
} from '../../components/tables/cell-renderers/active-media-plans';
import { parseErrorMsg } from '../../utils/listing-utils';
import { UserFilters, useUserFilters } from '../../components/tables/use-user-filters';
import { Loading } from '../../components/feedback/loading';
import { useUrlTableState } from '../../components/tables/table-state-persistence';
import { getCurrentViewState } from '../../components/tables/saved-views-utils';
import { PercentDifferenceIndicator } from '@/components/tables/cell-renderers/percent-difference-indicator';
import {
  marginDiscountDiff,
  marginVarianceDiff,
  unitCostVarianceDiff
} from '@/components/tables/cell-renderers/percent-difference-utils';
import { autoSizeStrategy } from '@/utils/ag-grid-utils';
import {
  EmptyPacingDetails,
  getValueString,
  NullPacingDetails
} from '@/features/line-items/line-item-table-cell-components';
import { cumulativeSpend } from 'shared/src/domain/line-item/pacing-helpers';
import { toISODateString } from 'shared/src/date-string-utils/date-utils';

export type CellRendererProps<T> = CustomCellRendererProps<AllLineItemTableData, T>;
export type TooltipProps<T> = CustomTooltipProps<AllLineItemTableData, T>;

export type AllLineItemTableData = LineItemTableRow & {
  pacing_graph: number[] | null;
  plan_status?: string; //We need this for plan status filter to work
};

export function isLineItemTotalsRow(row: AllLineItemTableData | undefined) {
  return row?.id === '__TOTAL_ROW__';
}

export function isLineItemDetailRow(
  row: LineItemTableRow | undefined
): row is AllLineItemTableData & { group: false } {
  return row?.group === false;
}

export function AllLineItems() {
  const userFilters = useUserFilters();
  return !userFilters ? <Loading /> : <AllLineItemsInner userFilters={userFilters} />;
}

function AllLineItemsInner({ userFilters }: { userFilters: UserFilters }) {
  const { initialState, storeSearch, storeGridState, getSearch } = useUrlTableState();
  const gridApi = useRef<GridApi<AllLineItemTableData>>(undefined);
  const { channels, platforms } = useChannelRatesContext();
  const columnDefs = useMemo(
    () => createColumnDefs(channels, platforms, userFilters),
    [channels, platforms, userFilters]
  );
  const navigate = useNavigate();
  useCaptureListingPageView('line_item');

  function handleRowClick(clickEvent: RowClickedEvent<LineItemTableRow>) {
    const { event, data } = clickEvent;
    if (!data || !data.id) return;
    const url = `/campaigns/${data.campaign_id}/execution/lineitems/${data.id}`;
    if (isMetaKeyPressed(event)) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  }

  function setSearchText(search: string) {
    storeSearch(search);
    gridApi.current?.refreshServerSide({ purge: true });
  }

  const getCurrentViewStateCallback = () => {
    return getCurrentViewState(gridApi, getSearch);
  };

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      gridApi.current.setGridOption('serverSideDatasource', {
        getRows: async params => {
          try {
            const sort = transformSortModel(params.request.sortModel);
            const filters = transformFilterModel(params.request.filterModel);
            const page = params.request.endRow ? Math.ceil(params.request.endRow / 50) : 1;
            const requestParams = {
              page,
              sort,
              filters,
              search: getSearch(),
              rowGroupCols: params.request.rowGroupCols || [],
              groupKeys: params.request.groupKeys
            };
            const { line_items, overall } = await client.allLineItemsV2.query(requestParams);
            const rows = getRowCount(line_items, overall);
            params.success({
              rowData: transformRows(line_items),
              rowCount: rows
            });
            if (!params.request.groupKeys || params.request.groupKeys.length == 0) {
              params.api.setGridOption('pinnedBottomRowData', [mapTotals(overall)]);
            }
          } catch (error) {
            console.error('Failed to fetch line items', error);
            showErrorToast(parseErrorMsg(error));
            params.success({ rowData: [], rowCount: 0 });
            return;
          }
        }
      });
    },
    [getSearch]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(() => {
    return {
      statusPanels: [{ statusPanel: TotalStatusPanel, align: 'left' }]
    };
  }, []);

  const setPins = useCallback((gridApi: GridApi) => {
    const isGrouped = (gridApi.getRowGroupColumns()?.length ?? 0) > 0;

    if (isGrouped) {
      // Unpin everything
      const allColIds = (gridApi.getAllGridColumns() ?? []).map(c => c.getColId());
      gridApi.applyColumnState({
        state: allColIds.map(colId => ({ colId, pinned: null })),
        defaultState: { pinned: null }
      });
      // Tell grid to keep detail rows, also used as flag to remove total row count
      gridApi.updateGridOptions({ keepDetailRows: true } as any);
    } else {
      // Pin line item name back on the left
      gridApi.applyColumnState({
        state: [{ colId: 'name', pinned: 'left' }]
      });
      // Don't keep detail rows, display total row count
      gridApi.updateGridOptions({ keepDetailRows: false } as any);
    }
  }, []);

  const onColumnRowGroupChanged = useCallback(
    (e: ColumnRowGroupChangedEvent) => {
      setPins(e.api);
    },
    [setPins]
  );

  const autoGroupColumnDef = {
    headerName: 'Group',
    minWidth: 200,
    maxWidth: 500,
    suppressHeaderMenuButton: true,
    sortable: false,
    resizable: true,
    cellRendererParams: {
      innerRenderer: ({ value, data }: CellRendererProps<string>) => {
        if (!data?.count) return '';

        return (
          <span className="flex items-center gap-2">
            {value ? (
              <>
                <span className="font-medium">{value}</span>
                <span className="text-sm text-gray-500">({data.count})</span>
              </>
            ) : (
              <span className="italic text-gray-400">none ({data.count})</span>
            )}
          </span>
        );
      },
      suppressCount: true
    }
  };

  return (
    <div className="flex h-full w-full flex-col px-8 pb-8 pt-3">
      <PageTitle title="Bravo | Line Items" />
      <SearchRow
        icon={<AllLineItemsIcon />}
        title="Line Items"
        defaultQuery={getSearch()}
        placeholder="Search By Line Item"
        submitSearch={setSearchText}
        tableType="line-items"
        getCurrentViewState={getCurrentViewStateCallback}
      />
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact
          rowGroupPanelShow={'onlyWhenGrouping'}
          groupDisplayType={'singleColumn'}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs}
          defaultColDef={{
            ...defaultColDef,
            suppressHeaderMenuButton: false,
            enableRowGroup: true
          }}
          rowBuffer={0}
          rowModelType={'serverSide'}
          cacheBlockSize={50}
          cacheOverflowSize={2}
          isExternalFilterPresent={() => true}
          infiniteInitialRowCount={50}
          maxBlocksInCache={10}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
          statusBar={statusBar}
          onRowClicked={handleRowClick}
          tooltipShowDelay={200}
          rowHeight={42}
          headerHeight={32}
          groupHeaderHeight={24}
          initialState={initialState}
          onStateUpdated={storeGridState}
          serverSideOnlyRefreshFilteredGroups={false}
          serverSideSortAllLevels={true}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelKey: 'columns',
                labelDefault: 'Columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressValues: true
                }
              },
              'filters'
            ]
          }}
        />
      </div>
    </div>
  );
}

function transformRows(rows: LineItemTableRow[]): AllLineItemTableData[] {
  return rows
    .filter(lineItem => !lineItem.is_deleted)
    .map(li => ({ ...li, pacing_graph: isLineItemDetailRow(li) ? generateGraph(li) : null }));
}

function createColumnDefs(
  channels: Channel[],
  platforms: Platform[],
  userFilters: UserFilters
): ColGroupDef<AllLineItemTableData>[] {
  const unitPriceTypes = allUnitPriceTypes(channels);
  const channelsWithTactics = allChannelsWithTactics(channels);
  const { mediaTraders, clientSuccessDirectors, accountManagers } = userFilters;
  return [
    {
      headerName: 'Line Item Details',
      openByDefault: false,
      children: [
        {
          field: 'name',
          headerName: 'Line Item Name',
          pinned: 'left',
          cellRenderer: cellRendererCmp('name'),
          cellStyle: { fontWeight: 'bold' },
          ...textFilter,
          maxWidth: 500
        },
        {
          field: 'line_item_type',
          headerName: 'Line Item Type',
          columnGroupShow: 'open',
          valueFormatter: params =>
            !params.data?.line_item_type
              ? ''
              : lineItemTypeDisplayNames[params.data.line_item_type] || '',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: Object.entries(lineItemTypeDisplayNames).map(([id, name]) => ({ id, name })),
            keyCreator: (params: KeyCreatorParams) => params.value.id,
            valueFormatter: (params: ValueFormatterParams) => params.value.name
          }
        },
        {
          field: 'price',
          headerName: 'Price',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value, 'No Price Set'),
          ...numberFilter
        },
        {
          cellDataType: 'date',
          field: 'start_date',
          headerName: 'Start Date',
          columnGroupShow: 'open',
          valueFormatter: params => dateValue(params.value, 'No Date Set'),
          filter: 'agDateColumnFilter',
          filterParams: {
            buttons: ['apply', 'reset'],
            filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
            maxNumConditions: 1
          }
        },
        {
          cellDataType: 'date',
          field: 'end_date',
          headerName: 'End Date',
          columnGroupShow: 'open',
          valueFormatter: params => dateValue(params.value, 'No Date Set'),
          filter: 'agDateColumnFilter',
          filterParams: {
            buttons: ['apply', 'reset'],
            filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
            maxNumConditions: 1
          }
        },
        {
          field: 'media_platforms',
          headerName: 'Media Platforms',
          columnGroupShow: 'open',
          cellRenderer: cellRendererCmp('media_platforms'),
          filter: 'agSetColumnFilter',
          filterParams: {
            values: platforms.map(p => p.id),
            valueFormatter: (params: ValueFormatterParams) =>
              platforms.find(p => p.id === params.value)?.name ?? String(params.value)
          },
          sortable: false
        },
        {
          field: 'channel_tactic',
          headerName: 'Channel: Tactic',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: channelsWithTactics,
            keyCreator: (params: KeyCreatorParams) => params.value.name,
            valueFormatter: (params: ValueFormatterParams) => params.value.name
          },
          valueGetter: params => {
            if (!params.data || isLineItemTotalsRow(params.data)) return null;
            if (!isLineItemDetailRow(params.data)) return params.data.channel_tactic;
            return `${params.data.channel_name}: ${params.data.tactic_name}`;
          }
        },
        {
          field: 'unit_price_type_name',
          headerName: 'Unit Price Type',
          columnGroupShow: 'open',
          minWidth: 200,
          cellRenderer: cellRendererCmp('unit_price_type_name'),
          filter: 'agSetColumnFilter',
          filterParams: {
            values: unitPriceTypes,
            keyCreator: (params: KeyCreatorParams) => params.value.id,
            valueFormatter: (params: ValueFormatterParams) => params.value.name
          }
        },
        {
          field: 'geo',
          headerName: 'Geo',
          columnGroupShow: 'open',
          cellRenderer: cellRendererCmp('geo'),
          ...textFilter
        },
        {
          field: 'targeting',
          headerName: 'Targeting',
          columnGroupShow: 'open',
          cellRenderer: cellRendererCmp('targeting'),
          ...textFilter
        },
        {
          field: 'audience',
          headerName: 'Audience',
          columnGroupShow: 'open',
          cellRenderer: cellRendererCmp('audience'),
          ...textFilter
        },
        {
          field: 'pacing_type',
          headerName: 'Pacing Type',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: { values: ['lifetime', 'custom'] },
          sortable: false
        },
        {
          field: 'monthly_rollover',
          headerName: 'Monthly Rollover',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: { values: [true, false] },
          sortable: false,
          valueFormatter: ({ value }) => (value ? 'Yes' : 'No')
        },
        {
          field: 'notes',
          headerName: 'Team Notes',
          columnGroupShow: 'open',
          cellRenderer: cellRendererCmp('notes'),
          ...textFilter
        },
        {
          field: 'pacing_graph',
          headerName: 'Pacing Details',
          columnGroupShow: 'open',
          minWidth: 200,
          sortable: false,
          cellRendererParams: { sparklineOptions: { type: 'area' } },
          cellRendererSelector: ({ data }) => {
            if (data?.pacing_details?.length === 0) return { component: EmptyPacingDetails };
            if (data?.pacing_details) return { component: 'agSparklineCellRenderer' };
            else return { component: NullPacingDetails };
          }
        }
      ]
    },
    {
      headerName: 'Campaign Details',
      openByDefault: false,
      children: [
        {
          field: 'campaign_name',
          headerName: 'Campaign Name',
          maxWidth: 500,
          cellRenderer: cellRendererCmp('campaign_name'),
          ...textFilter
        },
        {
          field: 'campaign_number',
          headerName: 'ID',
          columnGroupShow: 'open',
          cellRenderer: cellRendererCmp('campaign_number'),
          ...textFilter
        },
        {
          field: 'campaign_stage',
          headerName: 'Stage',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: zohoCampaignStatusValues
          }
        },
        {
          field: 'campaign_source',
          headerName: 'Marketing Source',
          columnGroupShow: 'open',
          maxWidth: 500,
          cellRenderer: cellRendererCmp('campaign_source'),
          ...textFilter
        },
        {
          field: 'account_name',
          headerName: 'Account',
          columnGroupShow: 'open',
          maxWidth: 400,
          cellRenderer: cellRendererCmp('account_name'),
          filter: 'agSetColumnFilter'
        },
        {
          field: 'agency_name',
          headerName: 'Agency',
          columnGroupShow: 'open',
          maxWidth: 400,
          cellRenderer: cellRendererCmp('agency_name'),
          filter: 'agSetColumnFilter'
        }
      ]
    },
    {
      headerName: 'Team',
      openByDefault: false,
      children: [
        {
          field: 'media_traders',
          headerName: 'Media Traders',
          valueFormatter: params => {
            return params.data?.media_traders?.map(mt => mt.name).join(',') || '';
          },
          sortable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: mediaTraders?.map(mt => mt.id) || [],
            valueFormatter: (params: ValueFormatterParams) =>
              mediaTraders?.find(mt => mt.id === params.value)?.name ?? params.value
          },
          enableRowGroup: false
        },
        {
          field: 'senior_media_trader',
          headerName: 'Senior Media Trader',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values:
              mediaTraders
                ?.filter(mt => seniorMediaTraderRoles.includes(mt.rep_type))
                .map(mt => mt.name) || []
          }
        },
        {
          field: 'lead_account_user',
          headerName: 'Lead Account Manager',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: accountManagers?.map(mt => mt.name) || []
          }
        },
        {
          field: 'client_success_director',
          headerName: 'Client Success Director',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: clientSuccessDirectors?.map(csd => csd.name) || []
          }
        }
      ]
    },
    {
      headerName: 'Line Item Plan',
      openByDefault: false,
      children: [
        {
          field: 'plan_status',
          headerName: 'Plan Status',
          cellRenderer: ({ data }: CellRendererProps<LineItemPlan>) => (
            <LineItemPlanStatusIndicator row={data} />
          ),
          filter: 'agSetColumnFilter',
          filterParams: {
            values: planStatusFilterValues,
            keyCreator: (params: KeyCreatorParams) => params.value.id,
            valueFormatter: (params: ValueFormatterParams) => params.value.name
          },
          tooltipComponent: LineItemPlanStatusTooltip,
          tooltipValueGetter: lineItemPlanStatusTooltipValue,
          resizable: false,
          sortable: false,
          enableRowGroup: false,
          width: 150
        }
      ]
    },
    {
      headerName: 'Media Activity',
      children: [
        {
          field: 'active_media_plans_count',
          headerName: 'Active Media Plans',
          cellRenderer: (props: CellRendererProps<number>) => (
            <ActiveLineItemMediaPlans data={props.data} />
          ),
          sortable: false,
          resizable: false,
          enableRowGroup: false,
          tooltipValueGetter: props => <ActiveLineItemMediaPlansTooltip data={props.data} />,
          tooltipComponent: ({ data, value }: TooltipProps<number>) => (
            <DoneOMeterTooltip value={value} isTotalsRow={isLineItemTotalsRow(data)} />
          ),
          filter: 'agSetColumnFilter',
          filterParams: {
            values: activePlatformBuysFilterValues,
            keyCreator: (params: KeyCreatorParams) => params.value.id,
            valueFormatter: (params: ValueFormatterParams) => params.value.name
          }
        }
      ]
    },
    {
      headerName: 'Line Item Delivery',
      openByDefault: false,
      children: [
        {
          field: 'delivery_pacing',
          headerName: 'Delivery Pacing',
          cellRenderer: (props: CellRendererProps<number>) => (
            <PacingIndicator value={props.value} />
          ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'percent_units_delivered',
          headerName: '% Units Delivered',
          cellRenderer: ({ value }: CellRendererProps<number>) => (
            <PercentCompletedIndicator percentValue={value} />
          ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'units_delivered',
          headerName: 'Units Delivered',
          columnGroupShow: 'open',
          cellRenderer: (props: CellRendererProps<number>) => {
            if (!props.data) return null;
            if (isLineItemTotalsRow(props.data)) return getNumberString(props.value || 0);
            const { unit_price_type_name } = props.data;
            return `${getNumberString(props.value || 0)} ${isLineItemDetailRow(props.data) ? getUnitName(unit_price_type_name!).abbr : ''}`;
          },
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'on_pace_units',
          headerName: 'On-Pace Units',
          columnGroupShow: 'open',
          valueFormatter: params => numericalValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'target_units',
          headerName: 'Target Units',
          columnGroupShow: 'open',
          valueFormatter: params => numericalValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'units_remaining',
          headerName: 'Units Remaining',
          columnGroupShow: 'open',
          valueFormatter: params => numericalValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        }
      ]
    },
    {
      headerName: 'Line Item Spend',
      openByDefault: false,
      children: [
        {
          field: 'spend_pacing',
          headerName: 'Spend Pacing',
          cellRenderer: (props: CellRendererProps<number>) => (
            <PacingIndicator value={props.value} />
          ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'percent_budget_spent',
          headerName: '% Budget Spent',
          cellRenderer: ({ value }: CellRendererProps<number>) => (
            <PercentCompletedIndicator percentValue={value} />
          ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'media_spend',
          headerName: 'Media Spend',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'on_pace_spend',
          headerName: 'On-Pace Spend',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'media_budget',
          headerName: 'Media Budget',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'budget_remaining',
          headerName: 'Budget Remaining',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false,
          enableRowGroup: false
        }
      ]
    },
    {
      headerName: 'Line Item Margin',
      openByDefault: false,
      children: [
        {
          field: 'margin_actual',
          headerName: 'Margin Actual',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <PacingIndicator value={value} colorBase={data?.planned_margin} />
          ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'margin_variance',
          headerName: 'Margin Variance',
          cellRenderer: ({ value, data }: CellRendererProps<number>) =>
            data?.margin_actual === 0 ? (
              <PendingPropertyIndicator
                label={
                  <div className="flex items-center gap-2">
                    Pending
                    <PlatformBuyIcon />
                  </div>
                }
              />
            ) : (
              <PercentDifferenceIndicator values={marginVarianceDiff(data?.margin_actual, value)} />
            ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'target_margin_variance',
          headerName: 'Margin Discount',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <PercentDifferenceIndicator values={marginDiscountDiff(data?.target_margin, value)} />
          ),
          ...percentFilter,
          enableRowGroup: false
        },
        {
          field: 'target_margin',
          headerName: 'Target Margin',
          columnGroupShow: 'open',
          valueFormatter: params => percentValue(params.value),
          ...percentFilter,
          enableRowGroup: false
        },
        {
          field: 'planned_margin',
          headerName: 'Planned Margin',
          columnGroupShow: 'open',
          valueFormatter: params => percentValue(params.value),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        }
      ]
    },
    {
      headerName: 'Line Item Cost',
      openByDefault: false,
      children: [
        {
          field: 'unit_cost_variance',
          headerName: 'Unit Cost Variance',
          cellRenderer: ({ value, data }: CellRendererProps<number>) =>
            data?.unit_cost_actual === 0 ? (
              <PendingPropertyIndicator
                label={
                  <div className="flex items-center gap-2">
                    Pending
                    <PlatformBuyIcon />
                  </div>
                }
              />
            ) : (
              <PercentDifferenceIndicator
                values={unitCostVarianceDiff(data?.unit_cost_actual, value)}
              />
            ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'unit_cost_actual',
          headerName: 'Unit Cost Actual',
          columnGroupShow: 'open',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => {
            if (isLineItemTotalsRow(data)) return null;
            if (valueDoesNotExist(value) || !data) return '--';
            const { unit_price_type_name } = data;
            let displayValue = value;
            if (unit_price_type_name === 'CPM') {
              displayValue *= 1000;
            }
            return `${formatCurrency(displayValue)} ${unit_price_type_name || ''}`;
          },
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'planned_unit_cost',
          headerName: 'Planned Unit Cost',
          columnGroupShow: 'open',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => {
            if (isLineItemTotalsRow(data)) return null;
            if (valueDoesNotExist(value) || !data) return '--';
            const { unit_price_type_name } = data;
            let displayValue = value;
            if (unit_price_type_name === 'CPM') {
              displayValue *= 1000;
            }
            return `${formatCurrency(displayValue)} ${unit_price_type_name || ''}`;
          },
          ...numberFilter,
          enableRowGroup: false
        }
      ]
    },
    {
      headerName: 'Line Item Revenue',
      openByDefault: false,
      children: [
        {
          field: 'percent_revenue_delivered',
          headerName: '% Revenue Delivered',
          cellRenderer: ({ value }: CellRendererProps<number>) => (
            <PercentCompletedIndicator percentValue={value} />
          ),
          ...percentFilter,
          resizable: false,
          enableRowGroup: false
        },
        {
          field: 'billable_revenue',
          headerName: 'Eff. Billable Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'uncapped_revenue',
          headerName: 'Uncapped Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'on_pace_revenue',
          headerName: 'On-Pace Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'target_revenue',
          headerName: 'Target Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'revenue_remaining',
          headerName: 'Revenue Remaining',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'monthly_capped_actual_revenue',
          headerName: 'Monthly Capped Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'effective_monthly_capped_actual_revenue',
          headerName: 'Eff. Monthly Capped Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'lifetime_capped_actual_revenue',
          headerName: 'Lifetime Capped Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        },
        {
          field: 'effective_lifetime_capped_actual_revenue',
          headerName: 'Eff. Lifetime Capped Revenue',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          enableRowGroup: false
        }
      ]
    }
  ];
}

function mapTotals(totals: LineItemOverallResult | null): Partial<LineItemListRow> {
  return {
    id: '__TOTAL_ROW__',
    campaign_number: '',
    price: totals?.overall_price || 0,
    start_date: totals?.earliest_start_date || undefined,
    end_date: totals?.latest_end_date || undefined,
    media_platforms: [],
    target_units: totals?.overall_target_units || 0,
    units_delivered: totals?.overall_actual_delivered_units || 0,
    percent_units_delivered: totals?.overall_percent_units_delivered || 0,
    on_pace_units: totals?.overall_on_pace_units || 0,
    units_remaining: totals?.overall_units_remaining || 0,
    delivery_pacing: totals?.overall_delivery_pacing || 0,
    media_spend: totals?.overall_actual_media_spend || 0,
    on_pace_spend: totals?.overall_on_pace_spend || 0,
    spend_pacing: totals?.overall_spend_pacing || 0,
    budget_remaining: totals?.overall_budget_remaining || 0,
    percent_budget_spent: totals?.overall_percent_budget_spent || 0,
    margin_actual: totals?.overall_margin_actual || 0,
    margin_variance: totals?.overall_margin_variance || 0,
    planned_margin: totals?.overall_planned_margin || 0,
    target_margin: totals?.overall_target_margin || 0,
    target_margin_variance: totals?.overall_target_margin_variance || 0,
    planned_unit_cost: totals?.overall_planned_unit_cost || 0,
    unit_cost_variance: totals?.overall_unit_cost_variance || 0,
    unit_cost_actual: totals?.overall_unit_cost_actual || 0,
    on_pace_revenue: totals?.overall_on_pace_revenue || 0,
    target_revenue: totals?.overall_target_revenue || 0,
    revenue_remaining: totals?.overall_revenue_remaining || 0,
    billable_revenue: totals?.overall_billable_revenue || 0,
    uncapped_revenue: totals?.overall_uncapped_revenue || 0,
    percent_revenue_delivered: totals?.overall_percent_revenue_delivered || 0,
    monthly_capped_actual_revenue: totals?.overall_monthly_capped_actual_revenue || 0,
    effective_monthly_capped_actual_revenue:
      totals?.overall_effective_monthly_capped_actual_revenue || 0,
    lifetime_capped_actual_revenue: totals?.overall_lifetime_capped_actual_revenue || 0,
    effective_lifetime_capped_actual_revenue:
      totals?.overall_effective_lifetime_capped_actual_revenue || 0,
    media_budget: totals?.overall_media_budget || 0
  };
}

function valueDoesNotExist(value: number | null | undefined) {
  return value === null || value === undefined;
}

function getNumberString(value: number | undefined): string {
  if (value === undefined) return '--';
  return value.toLocaleString();
}

function transformSortModel(sortModel: SortModelItem[]): LineItemSort | undefined {
  if (sortModel.length === 0) return undefined;
  const { sort, colId } = sortModel[0];
  const mappedColId = mapSortColumn(colId);
  if (lineItemSortColumns.includes(mappedColId)) {
    // TODO[mk]: properly refine type
    return { colId: mappedColId as LineItemSortColumn, sort };
  } else return undefined;
}

function transformFilterModel(filterModel: FilterModel | AdvancedFilterModel | null) {
  if (!filterModel) return {};
  const model = mapKeys(filterModel, (_, key) => mapFilterKey(key));
  return parseFilterModelPerKey(LineItemsFilterTypes, model);
}

function mapFilterKey(key: string): string {
  switch (key) {
    case 'name':
      return 'line_item_name';
    case 'plan':
      return 'plan_status';
    case 'active_media_plans_count':
      return 'active_platform_buys';
    default:
      return (key.split('.').pop() || key).replace(/_1$/, '');
  }
}

function mapSortColumn(key: string): string {
  return (key.split('.').pop() || key).replace(/_1$/, '');
}

function cellRendererCmp(key: keyof LineItemTableRow) {
  return (props: CustomCellRendererProps<AllLineItemTableData>) => {
    const value = getValueString(props.data?.[key]);
    return isLineItemTotalsRow(props.data) || (!isLineItemDetailRow(props.data) && !value)
      ? null
      : value || <div className="text-blue-500">-</div>;
  };
}

function isMetaKeyPressed(event: Event | null | undefined): boolean {
  return event instanceof PointerEvent && (event.metaKey || event.ctrlKey);
}

function getRowCount(lineItems: LineItemTableRow[], overall: LineItemOverallResult | null): number {
  // When grouping set the row count to -1 to remove the 'loading' icon from the grid
  return lineItems.length > 0 && lineItems[0].group ? -1 : overall?.total || 0;
}

export function generateGraph(lineItem: PartialLineItem): number[] {
  const { pacing_details } = lineItem;
  if (!pacing_details || pacing_details.length === 0 || !lineItem.start_date || !lineItem.end_date)
    return [];
  return cumulativeSpend(
    {
      from: toISODateString(lineItem.start_date),
      to: toISODateString(lineItem.end_date)
    },
    pacing_details
  ).map(day => day.cumulativeTotal);
}
